import React, { useContext } from "react";
import { AreaOfInterestType, ProcedureType, TScreen } from "api/type";

// Import Contexts
import { ClinicContext } from "contexts";
import ScreenContext from "contexts/screen";

// Import Utilities
import {
  getArmsProcedures,
  getBackProcedures,
  getBreastProcedures,
  getButtProcedures,
  getFaceProcedures,
  getHeadProcedures,
  getLegsProcedures,
  getTorsoProcedures,
  getVaginalProcedures,
} from "utils/getProceduresByAreaOfInterest";

// Import Components
import {
  NormalText,
  PrimaryButton,
  TitleBar,
  PickProcedure,
} from "components/index";
import { mixpanelTrack } from "utils/mixpanel";

type ScreenSelectProcedureProps = {
  selectedAreasOfInterest: AreaOfInterestType[];
  selectedProcedure: ProcedureType | null;
  handleSaveSelectedProcedure: (procedure: ProcedureType) => void;
};

const ScreenSelectProcedure: React.FC<ScreenSelectProcedureProps> = ({
  selectedAreasOfInterest,
  selectedProcedure,
  handleSaveSelectedProcedure,
}) => {
  // Contexts
  const clinic = useContext(ClinicContext);
  const setScreen = useContext(ScreenContext)[1];

  // State
  const [allowedProcedures, setAllowedProcedures] = React.useState<
    ProcedureType[]
  >([]);

  // Effect to update allowed procedures based on selected areas of interest
  React.useEffect(() => {
    if (!clinic) return;
    let allProcedures: ProcedureType[] = [];
    const clinicProcedures = clinic.procedures.map(
      (procedure) => procedure.type,
    );

    // Gather procedures based on the selected areas of interest
    selectedAreasOfInterest.forEach((area) => {
      if (area === AreaOfInterestType.ARMS)
        allProcedures = allProcedures.concat(getArmsProcedures());
      if (area === AreaOfInterestType.BACK)
        allProcedures = allProcedures.concat(getBackProcedures());
      if (area === AreaOfInterestType.BUTT)
        allProcedures = allProcedures.concat(getButtProcedures());
      if (area === AreaOfInterestType.FACE)
        allProcedures = allProcedures.concat(getFaceProcedures());
      if (area === AreaOfInterestType.LEGS)
        allProcedures = allProcedures.concat(getLegsProcedures());
      if (area === AreaOfInterestType.HEAD)
        allProcedures = allProcedures.concat(getHeadProcedures());
      if (area === AreaOfInterestType.TORSO)
        allProcedures = allProcedures.concat(getTorsoProcedures());
      if (area === AreaOfInterestType.BREAST)
        allProcedures = allProcedures.concat(getBreastProcedures());
      if (area === AreaOfInterestType.VAGINAL)
        allProcedures = allProcedures.concat(getVaginalProcedures());
    });

    // Filter allowed procedures based on clinic's available procedures
    allProcedures = Array.from(new Set(allProcedures));
    const allowedProcedures = allProcedures.filter((procedure) =>
      clinicProcedures.includes(procedure),
    );
    setAllowedProcedures(allowedProcedures);
  }, [clinic, selectedAreasOfInterest]);

  return (
    <div className="screen-container">
      <TitleBar
        name={"Select a Procedure"}
        onClickBackButton={() =>
          setScreen(TScreen.SELECT_AREA_OF_INTEREST_SCREEN)
        }
      />
      <NormalText
        textContent={"Pick one to visualize"}
        style={{ marginBottom: "auto" }}
      />
      <PickProcedure
        initialProcedure={selectedProcedure}
        allowedProcedures={allowedProcedures}
        aiProcedures={[
          ProcedureType.BREAST_AUGMENTATION,
          ProcedureType.BREAST_REDUCTION,
          ProcedureType.BREAST_LIFT,
          ProcedureType.BREAST_LIFT_AND_AUGMENTATION,
          ProcedureType.BBL,
          ProcedureType.TUMMY_TUCK,
          ProcedureType.LIP_FILLER,
        ]}
        onProcedureSelect={(selectedProcedure) => {
          handleSaveSelectedProcedure(selectedProcedure);
          mixpanelTrack("Procedure Selected", [
            {
              propertyName: "Clinic Name",
              propertyValue: clinic?.clinic_name ?? "failed to get clinic name",
            },
            { propertyName: "Procedure", propertyValue: selectedProcedure },
          ]);
        }}
      />
      <PrimaryButton
        text={"Continue"}
        disabled={!selectedProcedure}
        fullWidth
        onClick={() => setScreen(TScreen.SELECT_MODEL_SCREEN)}
      />
    </div>
  );
};

export default ScreenSelectProcedure;
